// To delete it when add sdk
export interface ProductMediaInfoDTO {
  urls: MediaInfoDTO[];
  isMain: boolean;
}

export interface MediaInfoDTO {
  widthInPx: number;
  heightInPx: number;
  formatType: string;
  sizeType: string;
  url: string;
}

interface ScrollOptions {
  top?: number;
  left?: number;
  behavior?: ScrollBehavior;
}

export const tooltipDelay = 300;
export const zeroPrice = 'MAD';

export const useUtils = () => {
  const scrollToTop = (options?: ScrollOptions) => {
    if (options) {
      window.scrollTo({
        top: options.top ?? 0,
        left: options.left ?? 0,
        behavior: options.behavior ?? 'smooth',
      });
    } else {
      document.documentElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getFormattedPrice = (price: number, currency: string, nbFractionDigits: number = 2) => {
    const amount = price || 0;

    // Define locale based on currency
    let locale = '';
    switch (currency) {
      case 'EUR':
        locale = 'fr-FR';
        break;
      case 'USD':
        locale = 'en-US';
        break;
      case 'CNY':
        locale = 'zh-CN';
        break;
      default:
        locale = 'en-US';
        break;
    }

    // Check if the amount is an integer (no decimal part)
    const hasDecimal = amount % 1 !== 0;

    // Create formatter with conditional decimal places
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: hasDecimal ? nbFractionDigits : 0, // If there's a decimal, show 2 decimals; otherwise, 0
      maximumFractionDigits: nbFractionDigits, // Customize decimal places
    });

    // Format and return the price
    return formatter.format(amount);
  };
  const getClosestBySomme = (number: number, array: MediaInfoDTO[]): MediaInfoDTO => {
    let current = array[0];
    const currentSome = array[0]?.heightInPx + array[0]?.widthInPx;

    let difference = Math.abs(number - currentSome);
    let index = array.length;
    while (index--) {
      const somme = array[index].heightInPx + array[index].widthInPx;
      const newDifference = Math.abs(number - somme);
      if (newDifference < difference) {
        difference = newDifference;
        current = array[index];
      }
    }
    return current;
  };

  const getClosestByHeight = (height: number, mediaList: MediaInfoDTO[]): MediaInfoDTO => {
    let current = mediaList[0];
    const currentSome = mediaList[0] ? mediaList[0].heightInPx : 0;

    let difference = Math.abs(height - currentSome);
    let index = mediaList.length;
    while (index--) {
      const value = mediaList[index].heightInPx;
      const newDifference = Math.abs(height - value);
      if (newDifference < difference) {
        difference = newDifference;
        current = mediaList[index];
      }
    }
    return current;
  };

  const getClosestByWidth = (number: number, array: MediaInfoDTO[]): MediaInfoDTO => {
    let current = array[0];
    const currentSome = array[0] ? array[0].widthInPx : 0;

    let difference = Math.abs(number - currentSome);
    let index = array.length;
    while (index--) {
      const value = array[index].widthInPx;
      const newDifference = Math.abs(number - value);
      if (newDifference < difference) {
        difference = newDifference;
        current = array[index];
      }
    }
    return current;
  };

  const getImageUrlByDimensions = (
    media: ProductMediaInfoDTO,
    width: number,
    height: number
  ): string => {
    const result = media.urls.filter((e) => e.widthInPx >= width && e.heightInPx >= height);
    const somme = width + height;
    let finalResult = media.urls[0];
    if (result.length > 0) {
      finalResult = getClosestBySomme(somme, result);
    } else {
      finalResult = getClosestBySomme(somme, media.urls);
    }
    return finalResult ? finalResult.url : '/img/product_placeholder.png';
  };

  const getImageUrlByWidth = (media: ProductMediaInfoDTO, width: number): string => {
    const result = media.urls.filter((e) => e.widthInPx >= width);
    let finalResult = media.urls[0];
    if (result.length > 0) {
      finalResult = getClosestByWidth(width, result);
    } else {
      finalResult = getClosestByWidth(width, media.urls);
    }
    return finalResult?.url;
  };

  const getImageUrlByHeight = (media: ProductMediaInfoDTO, height: number): string => {
    const result = media.urls.filter((e) => e.heightInPx >= height);
    let finalResult = media.urls[0];
    if (result.length > 0) {
      finalResult = getClosestByHeight(height, result);
    } else {
      finalResult = getClosestByHeight(height, media.urls);
    }
    return finalResult?.url;
  };

  const getImageUrlBySizeType = (media: ProductMediaInfoDTO, sizeType?: string): string => {
    const result = media.urls.find((e) => e.sizeType === sizeType);
    if (result) {
      return result.url;
    } else {
      return media.urls[0].url;
    }
  };

  const optimizeImageStoryblok = (image: string, option: string): string => {
    if (!image) {
      return '';
    }
    if (!option) {
      return '';
    }

    return `${image}/m/${option}`;
  };

  const downloadFileFromStoryblok = (url: string, filename: string): void => {
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'media-type',
    };
    const proxyUrl = 'https://s3.amazonaws.com/';
    const cleanedUrl = url ? url.replace('https://', '') : null;
    const UrlWithProxy = proxyUrl + cleanedUrl;

    fetch(UrlWithProxy, { headers, method: 'GET' }).then((response) => {
      return response.blob().then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', filename);
        a.click();
      });
    });
  };

  type FilterOption = {
    value: string;
    label: string;
    checked: boolean;
  };

  type Filter = {
    id: string;
    name: string;
    options: FilterOption[];
    type: string; // New property for the type
  };

  type Facets = {
    attributes: { id: string; name: string; values: string[] }[];
    suppliers?: { name: string }[];
    brands?: { name: string }[];
    productTags?: { id: string; name: string }[];
    customFields?: { name: string; values: string[] }[];
  };

  const transformDataToFilters = (filterKeys: string[] = [], data: Facets): Filter[] => {
    const filters: Filter[] = [];

    const addFilter = (id: string, name: string, options: FilterOption[], type: string) => {
      const sortedOptions = options.sort((a, b) => {
        const isNumericA = !isNaN(Number(a.value));
        const isNumericB = !isNaN(Number(b.value));

        if (isNumericA && isNumericB) {
          return Number(a.value) - Number(b.value); // Sort numerically if both are numbers
        } else {
          return a.label.localeCompare(b.label); // Sort alphabetically otherwise
        }
      });
      filters.push({ id, name, options: sortedOptions, type });
    };

    filterKeys.forEach((key) => {
      switch (key) {
        case 'brand':
          if (data.brands && data.brands.length > 0) {
            addFilter(
              'brand',
              'Brands',
              data.brands?.map((brand) => ({
                value: brand.name,
                label: brand.name,
                checked: false,
              })),
              'brand'
            );
          }
          break;
        case 'productTags':
          if (data.productTags && data.productTags.length > 0) {
            addFilter(
              'productTags',
              'Tags',
              data.productTags?.map((tag) => ({
                value: tag.id,
                label: tag.name,
                checked: false,
              })),
              'tag'
            );
          }
          break;
        default: {
          const attribute = data.attributes?.find((attr) => attr.id === key);
          if (attribute) {
            addFilter(
              attribute.id,
              attribute.name,
              attribute.values?.map((value) => ({
                value: value,
                label: value,
                checked: false,
              })),
              'attribute'
            );
          }
          break;
        }
      }
    });

    // Add any remaining filters not specified in filterKeys
    if (filterKeys.length === 0) {
      if (data.attributes && data.attributes.length > 0) {
        data.attributes.forEach((attribute) => {
          addFilter(
            attribute.id,
            attribute.name,
            attribute.values?.map((value) => ({
              value: value,
              label: value,
              checked: false,
            })),
            'attribute'
          );
        });
      }

      if (data.productTags && data.productTags.length > 0) {
        addFilter(
          'productTags',
          'Tags',
          data.productTags?.map((tag) => ({
            value: tag.id,
            label: tag.name,
            checked: false,
          })),
          'tag'
        );
      }

      if (data.customFields && data.customFields.length > 0) {
        data.customFields.forEach((customField) => {
          addFilter(
            customField.name,
            customField.name,
            customField.values?.map((value) => ({
              value: value,
              label: value,
              checked: false,
            })),
            'customField'
          );
        });
      }
    }

    return filters;
  };

  function transformToBreadcrumbData(category: any): Array<{ text: string; link: string }> {
    const breadcrumbs = [];
    let currentCategory = category;

    // Traverse up the category tree, adding each parent to the breadcrumbs array
    while (currentCategory) {
      // Skip adding "Root_default" as a breadcrumb
      if (currentCategory.externalId !== 'Root_default') {
        breadcrumbs.unshift({
          text: currentCategory.name || 'Undefined Category', // Use a placeholder if name is missing
          link: `/${currentCategory.externalId}`,
        });
      }
      currentCategory = currentCategory.parent; // Move up to the parent category
    }

    // Add homepage link as the first breadcrumb
    breadcrumbs.unshift({ text: "Page d'accueil", link: '/' });

    return breadcrumbs;
  }

  function getCapitalize(str: string): string {
    if (str) {
      return str.toLowerCase().replace(/(?:^|\s)\S/g, function (a) {
        return a.toUpperCase();
      });
    }
    return '';
  }

  return {
    scrollToTop,
    getClosestBySomme,
    getClosestByHeight,
    getClosestByWidth,
    getImageUrlByDimensions,
    getImageUrlByWidth,
    getImageUrlByHeight,
    getImageUrlBySizeType,
    optimizeImageStoryblok,
    downloadFileFromStoryblok,
    transformDataToFilters,
    transformToBreadcrumbData,
    getFormattedPrice,
    getCapitalize,
  };
};
